/**
 * 装修主题 token → CSS 变量注入
 *
 * 默认 token 与 admin/decoration_theme/index.vue 的 DEFAULT_TOKENS 对齐。
 * 启动时由 App.vue / main.js 调 fetchAndApplyTheme()：
 *   1. 调 GET /upms/shop/decoration/theme 拉激活主题
 *   2. 把 token 写入 :root 的 CSS 变量 --theme-*
 *   3. 全站 var(--theme-primary) 等会自动同步
 * 失败/无激活主题 → 走默认 token，业务不阻塞。
 */
import { getActiveTheme } from "@/api/upms/decoration";

const DEFAULT_TOKENS = {
  primary: "#4d6bfe",
  primaryHover: "#7388ff",
  success: "#67c23a",
  warning: "#e6a23c",
  danger: "#f56c6c",
  textPrimary: "#303133",
  textSecondary: "#606266",
  bgPage: "#f0f2f5",
  bgCard: "#ffffff",
  border: "#ebeef5",
  radiusBase: 4,
  fontBase: 14,
};

const VAR_MAP = {
  primary: "--theme-primary",
  primaryHover: "--theme-primary-hover",
  success: "--theme-success",
  warning: "--theme-warning",
  danger: "--theme-danger",
  textPrimary: "--theme-text-primary",
  textSecondary: "--theme-text-secondary",
  bgPage: "--theme-bg-page",
  bgCard: "--theme-bg-card",
  border: "--theme-border",
  radiusBase: "--theme-radius-base",
  fontBase: "--theme-font-base",
};

/** 把 token 对象写入 :root（默认作用 document.documentElement） */
export function applyThemeTokens(tokens) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const merged = Object.assign({}, DEFAULT_TOKENS, tokens || {});
  Object.keys(VAR_MAP).forEach(k => {
    const cssVar = VAR_MAP[k];
    const v = merged[k];
    if (v == null) return;
    if (k === "radiusBase" || k === "fontBase") {
      root.style.setProperty(cssVar, v + "px");
    } else {
      root.style.setProperty(cssVar, v);
    }
  });
  // 同步 legacy 变量（商品页/按钮等大量使用 --color-main，与 --theme-primary 对齐）
  const primary = merged.primary;
  const primaryHover = merged.primaryHover || primary;
  if (primary) {
    root.style.setProperty("--color-main", primary);
    root.style.setProperty("--color-submain", primary);
    root.style.setProperty("--color-price", primary);
    root.style.setProperty("--hover-color", primaryHover);
    root.style.setProperty("--active-button", primaryHover);
    root.style.setProperty("--bg-border-color", primary);
    root.style.setProperty("--color-footer", primary);
  }
}

/** 启动时拉激活主题并应用；先用默认 token 占位，再异步覆盖 */
export function fetchAndApplyTheme() {
  applyThemeTokens(DEFAULT_TOKENS);
  return getActiveTheme().then(res => {
    const data = res && res.data && res.data.data;
    if (!data) {
      return;
    }
    let tokens = null;
    try { tokens = data.configurationJson ? JSON.parse(data.configurationJson) : null; } catch (e) { tokens = null; }
    if (data.primaryColor) {
      tokens = Object.assign({ primary: data.primaryColor }, tokens || {});
    }
    if (tokens && typeof tokens === "object") {
      applyThemeTokens(tokens);
    }
  }).catch(() => {
  });
}

export { DEFAULT_TOKENS };
